import {
  dateFormatAU,
  EVENT_TIME_DEFAULT,
  fractionalHourTohhmm,
  hhcmmToFractionalHour,
  hourToAMPM,
  minutesToHHMM,
  zeroPad,
} from "../utils/datetime";
import { DanceEventScheduleCategory, DanceEventScheduleItem } from "../types";

import "./timeline.scss";
import { CSSProperties } from "preact/compat";
import { Tooltip } from "@mui/material";

const itemStartTimes = (
  items?: DanceEventScheduleItem[],
  openTime?: string
) => {
  const startTimes =
    items?.filter((item) => item.start).map((item) => item.start) || [];
  if (openTime) {
    startTimes.push(openTime);
  }
  return startTimes;
};

export const scheduleItemSort = (
  a: DanceEventScheduleItem,
  b: DanceEventScheduleItem
) => {
  const delta = (b.day || 0) - (a.day || 0);
  if (delta !== 0) {
    return delta;
  }
  return (a.start || "0000").localeCompare(b.start || "0000");
};

export const scheduleItemCategorySort = (
  a: DanceEventScheduleCategory,
  b: DanceEventScheduleCategory
) => {
  const orderDelta = a.order - b.order;
  if (orderDelta !== 0) {
    return orderDelta;
  }
  return a.name?.localeCompare(b.name || "") || 0;
};

export const timelineDayLabel = (thisDate: Date, day: number) => {
  thisDate.setDate(thisDate.getDate() + day);
  return (
    <strong>
      Day {day + 1} - {dateFormatAU(thisDate, true)}
    </strong>
  );
};

const itemEndTimes = (items?: DanceEventScheduleItem[]) => {
  const itemEndTime = (item: DanceEventScheduleItem) => {
    if (!(item.start && item.duration)) {
      return;
    }
    let [hh, mm] = (
      item.start.includes(":")
        ? item.start.split(":")
        : [item.start.substring(0, 2), item.start.substring(2, 4)]
    ).map((part) => parseInt(part));

    mm += item.duration;
    while (mm >= 60) {
      mm -= 60;
      hh = (hh + 1) % 24;
    }
    return zeroPad(hh) + ":" + zeroPad(mm);
  };

  return (items?.map((item) => itemEndTime(item)).filter((end) => end) ||
    []) as string[];
};

const getScheduleStartHour = (
  items?: DanceEventScheduleItem[],
  openTime?: string
) => {
  const startTimes = itemStartTimes(items, openTime);
  if (startTimes.length > 0) {
    return Math.min(...startTimes.map((time) => parseInt(time as string)));
  }
  return parseInt(EVENT_TIME_DEFAULT);
};

const getScheduleEndHour = (items?: DanceEventScheduleItem[]) => {
  const endTimes = itemEndTimes(items);

  if (endTimes.length > 0) {
    return Math.max(
      ...endTimes.map((time) => hhcmmToFractionalHour(time as string))
    );
  }
  return parseInt(EVENT_TIME_DEFAULT) + 3;
};

const hhcmmToAmPm = (hhcmm: string) => {
  if (hhcmm.indexOf(":") > 0) {
    const [openHH, openMM] = hhcmm.split(":").map((a) => parseInt(a));
    return hourToAMPM(openHH, openMM);
  }
  return hourToAMPM(
    parseInt(hhcmm.substring(0, 2)),
    parseInt(hhcmm.substring(2, 4))
  );
};

export const getTimeDivisions = (
  items?: DanceEventScheduleItem[],
  openTime?: string
) => {
  const spread = (length: number) =>
    Array.from({ length }).map((_, i) => zeroPad((i * 60) / length));

  const times = new Set(
    items
      ?.filter((item) => item.start)
      .flatMap((item) => {
        const minutes = parseInt(item.start?.split(":")[1] || "");
        if (item.duration) {
          return [minutes, (minutes + item.duration) % 60];
        }
        return [minutes];
      }) || []
  );
  if (openTime) {
    times.add(parseInt(openTime.split(":")[1]));
  }

  times.delete(0);
  if (times.size < 1) {
    return spread(1);
  }

  const timesArr = Array.from(times);

  if (timesArr.every((time) => [30].includes(time))) {
    return spread(2);
  }

  if (timesArr.every((time) => [20, 40].includes(time))) {
    return spread(3);
  }

  if (timesArr.every((time) => [15, 30, 45].includes(time))) {
    return spread(4);
  }

  if (timesArr.every((time) => [10, 20, 30, 40, 50].includes(time))) {
    return spread(6);
  }
  return spread(12);
};

export const DanceEventScheduleTimeline = (props: {
  scheduleItems?: DanceEventScheduleItem[];
  itemCategories?: Record<string, DanceEventScheduleCategory>;
  openTime?: string;
  startHour?: number;
  onItemClick?: (id: string) => void;
}) => {
  const scheduledStartHour = getScheduleStartHour(
    props.scheduleItems,
    props.openTime
  );

  const endHour = getScheduleEndHour(props.scheduleItems);
  const endMinute = (endHour % 1) * 60;

  const timeDivision = getTimeDivisions(props.scheduleItems, props.openTime);

  const scheduleItemOpen = Object.values(props.itemCategories || {}).sort(
    scheduleItemCategorySort
  )[0];

  // If startHour is set, assume we're aligning multiple days
  const gridTemplateUnit = props.startHour
    ? 12 / timeDivision.length + "em"
    : "1fr";

  const doorsOpenSlot = () => {
    if (!props.openTime) {
      return;
    }
    const startRow = "time-" + props.openTime.replace(":", "");
    const closeRow = "time-" + fractionalHourTohhmm(endHour);

    const firstItemStart = Math.min(
      ...(
        props.scheduleItems
          ?.filter((item) => item.start)
          .map((item) => item.start) || []
      ).map((start) => hhcmmToFractionalHour(start as string))
    );

    const endRow = "time-" + fractionalHourTohhmm(firstItemStart);

    return (
      <>
        <div
          class="timeline-item timeline-open"
          style={{
            gridRow: startRow + " / " + closeRow,
            borderTop: "0",
            backgroundColor: scheduleItemOpen?.colour,
          }}
        ></div>
        <div
          class="timeline-item timeline-open"
          style={{
            gridRow: startRow + " / " + endRow,
          }}
        >
          Doors Open - {hhcmmToAmPm(props.openTime)}
        </div>
      </>
    );
  };

  const renderItem = (item: DanceEventScheduleItem, compact = false) => {
    if (!item.start) {
      return;
    }
    const duration = item.duration || 60;
    const startRow = "time-" + item.start.replace(":", "");
    let [endHour, endMin] = item.start.split(":").map((a) => parseInt(a));
    endMin += duration;
    endHour += Math.floor(endMin / 60);
    endMin %= 60;
    const endRow = "time-" + zeroPad(endHour) + zeroPad(endMin);

    const style: CSSProperties = {
      gridRow: startRow + " / " + endRow,
    };

    const tooltipStyle: CSSProperties = {};

    if (item.category && props.itemCategories?.[item.category.id]) {
      style.backgroundColor = props.itemCategories?.[item.category.id].colour;
      tooltipStyle.backgroundColor =
        props.itemCategories?.[item.category.id].colour;
    }

    const classNames = ["timeline-item", "timeline-entry"];
    if (props.onItemClick) {
      classNames.push("has-onclick");
    }
    const onClick = () => {
      if (props.onItemClick && item.id) {
        props.onItemClick(item.id);
      }
    };

    const durationStr = item.duration
      ? " (" + minutesToHHMM(item.duration) + ")"
      : "";

    const timeStr =
      hhcmmToAmPm(item.start) +
      " - " +
      hourToAMPM(endHour, endMin) +
      durationStr;

    const _inner = (compact: boolean) => {
      if (item.duration && item.duration < 30 && !item.teachers) {
        return (
          <div class={classNames.join(" ")} style={style} onClick={onClick}>
            <div>
              <strong>{item.name}</strong>: {timeStr}
            </div>
          </div>
        );
      }

      const longBlock =
        item.duration &&
        item.duration >= (item.description?.length || 0) / 2 - 30;

      const descriptionClass = compact && !longBlock ? "ellipsis" : "pre-line";

      return (
        <div class={classNames.join(" ")} style={style} onClick={onClick}>
          <div>
            <strong>{item.name}</strong>
          </div>
          {!compact && <div>{timeStr}</div>}
          {!compact && item.teachers && <div>{item.teachers}</div>}
          {item.description && (
            <div class={descriptionClass}>{item.description}</div>
          )}
        </div>
      );
    };

    if (compact && typeof window !== "undefined") {
      return (
        <Tooltip
          enterTouchDelay={0}
          leaveTouchDelay={0}
          title={
            <div style={tooltipStyle} class="tooltip">
              {_inner(false)}
            </div>
          }
        >
          {_inner(compact)}
        </Tooltip>
      );
    }
    return _inner(compact);
  };

  const timeTick = (time: string, index: number) => {
    const className = () => {
      const mod = index % timeDivision.length;
      if (mod === 0) {
        return "main";
      }
      if (mod % 2 === 1) {
        return "minor";
      }
      return "";
    };
    return (
      <div
        class={"time-tick " + className()}
        style={{ gridRow: "time-" + time }}
      />
    );
  };

  const _inner = (startHour: number, fixedHeight = false) => {
    const hourCount = endHour - startHour + (endHour < startHour ? 24 : 0) + 1;
    const hours = Array.from({ length: hourCount }).map(
      (_, hour) => hour + startHour
    );

    const gridHours = hours.flatMap((hour, i, arr) => {
      if (i + 1 >= arr.length) {
        // final hour
        return timeDivision
          .filter((min) => parseInt(min) <= endMinute)
          .map((minute) => zeroPad(hour) + minute);
      }
      return timeDivision.map((minute) => zeroPad(hour) + minute);
    });
    const compact = gridHours.length >= 20;

    const gridTemplateRows = gridHours
      .map((timestamp) => "[time-" + timestamp + "] " + gridTemplateUnit)
      .join("\n");

    return (
      <div
        class={"event-schedule-timeline" + (fixedHeight ? " fixed-height" : "")}
        style={{ gridTemplateRows: gridTemplateRows }}
      >
        {gridHours.map(timeTick)}
        {hours.map((hour) => (
          <div
            class="time-label"
            style={{ gridRow: `time-${zeroPad(hour)}00` }}
          >
            {hourToAMPM(hour)}
          </div>
        ))}

        {doorsOpenSlot()}

        {props.scheduleItems?.map((item) => renderItem(item, compact))}
      </div>
    );
  };

  // potentially render twice, to align starts on desktop but not mobile
  if (props.startHour && props.startHour !== scheduledStartHour) {
    return (
      <>
        <div class="hide-under-992">{_inner(props.startHour, true)}</div>
        <div class="hide-over-992">{_inner(scheduledStartHour, false)}</div>
      </>
    );
  }
  return _inner(props.startHour || scheduledStartHour, !!props.startHour);
};

export const DanceEventScheduleTimelineKey = (props: {
  scheduleItems: DanceEventScheduleItem[];
  itemCategories: Record<string, DanceEventScheduleCategory>;
}) => {
  const openCategory = Object.values(props.itemCategories || {}).sort(
    scheduleItemCategorySort
  )[0];
  const activeCategories = Object.fromEntries(
    props.scheduleItems
      ?.filter((item) => item.category && item.category.id !== openCategory.id)
      .map((item) => props.itemCategories[item.category!.id])
      .sort(scheduleItemCategorySort)
      .map((item) => [item.id, item])
  ) as Record<string, DanceEventScheduleCategory>;

  if (!activeCategories) {
    return <></>;
  }

  const renderCategory = (cat: DanceEventScheduleCategory) => {
    return (
      <span class="category">
        <span class="swatch" style={{ backgroundColor: cat.colour }}>
          &nbsp;
        </span>
        {cat.name}
      </span>
    );
  };

  return (
    <div class="timeline-key-wrap">
      <div class="timeline-key">
        <strong class="category">Key:</strong>
        {openCategory && renderCategory(openCategory)}
        {Object.values(activeCategories).map(renderCategory)}
      </div>
    </div>
  );
};
